import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { subscriptionEmployeeSubscriptionItemBlockade } from "src/utils/RoleBasedSecurity/permissions";
import Form from "src/Components/Form";
import { Alert } from "reactstrap";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { restApiRequest, SUBSCRIPTION_MANAGEMENT_SERVICE } from "src/utils/Api";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import DefaultTooltip from "src/Components/Tooltips/DefaultTooltip";

const Block = ({ subscription, close }) => {
  const [data, setData] = useState({});
  const onChange = useCallback(
    (key, value) => {
      const updatedData = { ...data };
      updatedData[key] = value;
      setData(updatedData);
    },
    [data]
  );

  const formatMonthYear = (isoString) => {
    const d = new Date(isoString);
    const month = d.toLocaleString("en-US", {
      month: "2-digit",
      timeZone: "Europe/Warsaw",
    });
    const year = d.toLocaleString("en-US", {
      year: "numeric",
      timeZone: "Europe/Warsaw",
    });
    return `${month}/${year}`;
  };

  const defaultUntil = subscription.blockade?.defaultUntil
    ? formatMonthYear(subscription.blockade.defaultUntil)
    : null;

  const switcherLabel = defaultUntil ? (
    <>
      {__("Użyj z ustawień benefitu")}
      <DefaultTooltip
        id="blockade-default-until-tooltip"
        content={`${__("Data z ustawień benefitu")}: ${defaultUntil}`}
      />
    </>
  ) : (
    "Użyj z ustawień benefitu"
  );

  useEffect(() => {
    setData({
      blockTo: subscription.blockade?.effectiveUntil ?? subscription.blockTo,
      useDefaultBlockDate: subscription.blockade?.usesDefault ?? false,
    });
  }, [subscription]);

  const submit = async () => {
    try {
      const { subscriptionItemId } = subscription;
      await restApiRequest(
        SUBSCRIPTION_MANAGEMENT_SERVICE,
        `/employee-subscription-items/${subscriptionItemId}/blockade`,
        "PUT",
        {
          body: data.useDefaultBlockDate
            ? { blockToIsDefault: true }
            : { blockTo: data.blockTo, blockToIsDefault: false },
        },
        null
      );
      dynamicNotification(__("Blokada została ustawiona."));
      close(true);
    } catch (e) {
      console.error(e);
      newRelicErrorReport(
        e,
        "Pages/Company/EmployeeManagement/CompanyEmployees/Edit/Subscriptions/Popup/block.js - 43"
      );
      dynamicNotification(
        e.message || __("Nie udało się ustawić blokady."),
        "error"
      );
    }
  };
  return (
    <Form
      id="blockSubscriptionForm"
      data={data}
      config={{
        isInPopup: true,
        togglePopup: () => close(),
        title: "Blokada na ponowne przystąpienie do świadczenia",
        onSubmit: submit,
        buttons: [
          {
            onClick: () => close(),
            text: "Zamknij",
            color: "light",
            id: "resetOneTimeFormClose",
          },
          {
            text: "Zmień",
            type: "submit",
            id: "resetOneTimeFormSubmit",
            permission: subscriptionEmployeeSubscriptionItemBlockade,
          },
        ],
        defaultOnChange: onChange,
        noCards: true,
        formGroups: [
          {
            formElements: [
              {
                component: <Info />,
              },
              {
                id: "blockTo",
                label: "Blokada ponownego wyboru do:",
                type: "month",
                tooltip: {
                  content: __(
                    "Pozostaw puste aby nie blokować ponownego wyboru tego świadczenia."
                  ),
                },
                inputSwitcher: {
                  onChange: (field, checked) => {
                    setData((prev) => ({
                      ...prev,
                      useDefaultBlockDate: checked,
                      blockTo: checked
                        ? subscription.blockade?.defaultUntil ?? null
                        : subscription.blockade?.effectiveUntil ??
                          subscription.blockTo,
                    }));
                  },
                  disableIfChecked: true,
                  switcherValue: Boolean(data?.useDefaultBlockDate === true),
                  label: switcherLabel,
                  under: true,
                },
              },
            ],
          },
        ],
      }}
    />
  );
};

const Info = () => (
  <Alert key="cancelInfo" color="primary">
    <p>
      {__(`Ustawienie blokady na ponowne przystąpienie do świadczenia spowoduje,
         że użytkownik będzie mógł wybrać świadczenie od następnego miesiąca po dacie wskazanej w polu „blokada ponownego wyboru do”.
         Jeśli na konfiguracji benefitu ustawiona jest blokada na ponowne przystąpienie, a chcesz ją usunąć lub zmienić dla użytkownika,
         odznacz checkbox “Użyj z ustawień benefitu”. W przypadku reaktywacji świadczenia, czyszczona jest informacja dotycząca daty blokady.`)}
    </p>
  </Alert>
);

Block.propTypes = {
  close: PropTypes.func.isRequired,
  subscription: PropTypes.shape({
    id: PropTypes.string,
    subscriptionItemId: PropTypes.string,
    blockTo: PropTypes.string,
    blockade: PropTypes.shape({
      usesDefault: PropTypes.bool,
      defaultUntil: PropTypes.string,
      effectiveUntil: PropTypes.string,
    }),
  }).isRequired,
};

export default Block;
